<!--
Presentational component for displaying the loading status of a slide deck
-->
<template>
	<figure
		class="relative h-42.75 w-56.25 overflow-hidden rounded-xl"
		:class="{
			'border-primary-700 border-[3px] shadow-xl': selected,
		}"
	>
		<!-- Background -->
		<div
			class="absolute inset-0 bg-cover"
			:class="{
				'blur-xl': downloadProgress !== undefined || error,
				'contrast-[.4] grayscale': inactive,
				'-inset-0.75!': selected,
			}"
			:style="`background-image: url(${imageUrl || 'https://placehold.co/215x171'});`"
		></div>

		<!-- Checkmark - Draw outline over solid -->
		<div
			v-if="selected"
			class="absolute top-2 right-2 h-6 w-6 *:absolute *:inset-0"
		>
			<CheckCircleIconSolid class="fill-primary-700 z-10" />
			<CheckCircleIconOutline class="z-20 stroke-white" />
		</div>

		<div class="absolute inset-0 grid place-content-center">
			<ExclamationCircleIcon
				v-if="error"
				class="h-8 w-8 fill-red-500 stroke-white"
			/>
			<ProgressCircle
				v-else-if="downloadProgress !== undefined"
				:progress="downloadProgress"
			/>
		</div>

		<!-- New tag -->
		<div
			v-if="fresh"
			class="bg-accent-500 text-secondary-50 absolute top-2 left-2 rounded-md px-2 py-1 font-sans text-base font-medium tracking-tight uppercase"
		>
			New
		</div>

		<!-- Title -->
		<figcaption
			class="text-secondary-50 absolute line-clamp-2 rounded-md px-2 py-1 font-sans text-base font-medium tracking-tight"
			:class="[
				selected ? 'bg-primary-700 bottom-2 left-2' : 'bottom-2.75 left-2.75',
			]"
			data-test-id="deck-thumbnail-title"
			:title="title"
		>
			{{ title }}
		</figcaption>
	</figure>
</template>

<script setup lang="ts">
import ProgressCircle from '@/components/common/ProgressCircle.vue'
import {
	CheckCircleIcon as CheckCircleIconOutline,
	ExclamationCircleIcon,
} from '@heroicons/vue/24/outline'
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/vue/24/solid'

const props = defineProps({
	imageUrl: String,
	selected: Boolean,
	inactive: Boolean,
	error: Boolean,
	fresh: Boolean,
	downloadProgress: {
		type: Number,
		default: undefined,
	},
	title: String,
})

/** Expose props for unit tests */
defineExpose({
	selected: props.selected,
	inactive: props.inactive,
})
</script>
